import { useCallback, useRef, useState } from 'react'
import { usePostPolarisStatusLookup } from '../utils/api-client/api-hooks.ts'
import type { IntegrationType, PolarisStatusResponse, SkippedEntry } from '../utils/api-client/api-schemas.ts'
import { getIsPolarisConfirmationRequired, POLARIS_STATUS_UNAVAILABLE_MESSAGE } from '../utils/polaris-status.ts'

/**
 * What a pending push still needs the user to confirm.
 *
 * A union because either Polaris answered or it did not: a request that never reached Polaris is a
 * client-side failure rather than a state Polaris reported, and the two describe the same targets in
 * incompatible ways. Folding the failure into a synthetic `lookup-failed` status row made them
 * indistinguishable to the dialog; carrying it as an extra optional field let both be populated at
 * once, which only the hook's own discipline prevented.
 */
export type PolarisPushConfirmation =
  | { state: 'reviewed'; statuses: PolarisStatusResponse[]; skipped: SkippedEntry[] }
  | { state: 'lookup-failed'; reason: string }

/**
 * Everything the confirmation dialog needs, or `undefined` when no push is waiting on the user.
 *
 * Handed back as one object so each surface that pushes wires one value through instead of four, and
 * so "there is a pending confirmation" and "the dialog is open" cannot drift apart.
 */
export type PolarisPushConfirmationDialogProps = PolarisPushConfirmation & {
  onConfirm: () => unknown
  onCancel: () => unknown
  /** Whether the push the user confirmed is still running, so the dialog can say so. */
  isConfirming?: boolean
}

/** Strips the hook's plumbing back off, leaving what the dialog is allowed to see. */
const confirmationOf = (pending: PendingConfirmation): PolarisPushConfirmation =>
  pending.state === 'reviewed'
    ? { state: 'reviewed', statuses: pending.statuses, skipped: pending.skipped }
    : { state: 'lookup-failed', reason: pending.reason }

type PendingConfirmation = PolarisPushConfirmation & {
  /** Runs the push the user was asked about, over every target they selected. */
  push: () => Promise<unknown>
  /** Settles once that push has run or been declined. @see preflight */
  promise: Promise<undefined>
  settle: () => unknown
}

/**
 * Runs the Polaris status check that precedes a triage push, and holds the user's answer.
 *
 * Shared by the single push in the finding drawer and the bulk push in the findings table. Both ran
 * their own copy of this flow, down to the same fallback message, and had already begun to drift.
 *
 * `preflight` does not resolve until the push has either run or been declined. Callers treat a
 * resolved push as "the action is over" — the findings table clears the user's row selection on it —
 * so resolving as soon as the dialog opened pulled the selection out from under them mid-decision.
 *
 * @param integrationType the producing integration's type. Only Polaris carries remote triage state
 *   a push could overwrite, so every other integration skips the check rather than paying a
 *   round-trip to confirm a status irrelevant to it.
 * @param push the push mutation's `mutateAsync`. Owned here rather than called by each caller after
 *   the fact, so "confirmed" and "pending" are one state machine instead of one per call site.
 */
export const usePolarisPushPreflight = ({
  integrationType,
  push,
}: {
  integrationType?: IntegrationType
  push: (variables: { triageResultIds: string[] }) => Promise<unknown>
}) => {
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation>()
  const [isPushInFlight, setIsPushInFlight] = useState(false)
  /**
   * The preflight currently running, from the first call until the push it leads to has settled.
   *
   * A ref rather than state because it guards against a second call arriving before React has
   * re-rendered — the whole window the guard exists for is one in which no state update has been
   * observed yet.
   */
  const inFlightPreflightRef = useRef<Promise<void> | undefined>(undefined)
  const statusLookupMutation = usePostPolarisStatusLookup()

  const runPush = useCallback(
    async (triageResultIds: string[]) => {
      setIsPushInFlight(true)
      try {
        await push({ triageResultIds })
      } finally {
        setIsPushInFlight(false)
      }
    },
    [push]
  )

  /**
   * Runs one push start to finish: check Polaris, ask the user if anything needs confirming, push.
   *
   * Kept separate from `preflight` so the dedupe guard lives in exactly one place and this can read as
   * a straight line.
   */
  const runPreflight = useCallback(
    async (triageResultIds: string[]) => {
      if (integrationType !== 'polaris') {
        await runPush(triageResultIds)
        return
      }

      let confirmation: PolarisPushConfirmation | undefined
      try {
        const response = await statusLookupMutation.mutateAsync({ triageResultIds })
        const statuses = response.statuses.filter(status => getIsPolarisConfirmationRequired(status.state))
        // `undefined` for "nothing to confirm", with the push itself deliberately left until after the
        // `catch`: a push that failed in here would be reported to the user as a failed *lookup*, and
        // then run a second time if they answered "Push anyway".
        confirmation =
          statuses.length === 0 && response.skipped.length === 0
            ? undefined
            : { state: 'reviewed', statuses, skipped: response.skipped }
      } catch (error) {
        // Surfaced rather than swallowed: the user is about to decide whether to overwrite Polaris
        // state, and "Polaris returned 503" versus "our own response parse failed" warrant different
        // confidence in doing so.
        confirmation = {
          state: 'lookup-failed',
          reason: error instanceof Error ? error.message : POLARIS_STATUS_UNAVAILABLE_MESSAGE,
        }
      }

      if (confirmation === undefined) {
        await runPush(triageResultIds)
        return
      }

      // Awaited rather than returned, so this call is not over until the push has run or been declined:
      // the findings table clears the user's row selection once it resolves.
      const { promise, resolve } = Promise.withResolvers<undefined>()
      setPendingConfirmation({
        ...confirmation,
        // Every selected target, not just the ones being confirmed: the user is approving the whole
        // push, and the statuses only explain which parts of it could not be verified.
        push: () => runPush(triageResultIds),
        promise,
        settle: () => {
          resolve(undefined)
        },
      })
      await promise
    },
    [integrationType, runPush, statusLookupMutation.mutateAsync]
  )

  const preflight = useCallback(
    ({ triageResultIds }: { triageResultIds: string[] }) => {
      if (triageResultIds.length === 0) {
        return Promise.resolve()
      }

      // A second request joins the first rather than starting its own: two dialogs cannot be open at
      // once, and the first caller is awaiting a promise only its own confirmation settles, so
      // replacing it would leave that promise pending forever — permanently stranding whatever it
      // protects (the findings table's row selection). The ref is written synchronously below, before
      // the lookup can settle, so a second call arriving before React has re-rendered the button that
      // triggered it still sees it.
      const outstanding = inFlightPreflightRef.current
      if (outstanding) {
        return outstanding
      }

      const started = runPreflight(triageResultIds)
        .catch(() => {
          // Swallowed deliberately, matching `confirm`: the only rejection that reaches here is the
          // push's own, which `usePostPush`'s `onError` has already reported. Both call sites invoke
          // this straight from a click handler, so rejecting would add an unhandled rejection to every
          // failed push.
        })
        .finally(() => {
          inFlightPreflightRef.current = undefined
        })
      inFlightPreflightRef.current = started
      return started
    },
    [runPreflight]
  )

  const confirm = useCallback(async () => {
    if (!pendingConfirmation) {
      return
    }
    try {
      await pendingConfirmation.push()
    } catch {
      // Swallowed deliberately: the push mutation's own `onError` already told the user, and this
      // promise is wired straight to the dialog's button, so rejecting here would add an unhandled
      // rejection to every failed confirmed push. The caller's promise still settles below.
    } finally {
      pendingConfirmation.settle()
      setPendingConfirmation(undefined)
    }
  }, [pendingConfirmation])

  const cancel = useCallback(() => {
    // Nothing to cancel once the confirmed push is in flight: closing the dialog then would report
    // the push as declined while it completes anyway. `confirm` settles and clears it on its own.
    if (isPushInFlight) {
      return
    }
    pendingConfirmation?.settle()
    setPendingConfirmation(undefined)
  }, [isPushInFlight, pendingConfirmation])

  return {
    preflight,
    /**
     * The dialog's props, or `undefined` when nothing is pending. The hook's own plumbing (the
     * caller's promise and the push it holds) stays out of the spread.
     */
    confirmationDialogProps: pendingConfirmation
      ? {
          ...confirmationOf(pendingConfirmation),
          onConfirm: confirm,
          onCancel: cancel,
          isConfirming: isPushInFlight,
        }
      : undefined,
    /**
     * Whether a push is underway from the user's point of view — including while the dialog waits on
     * their answer, so the action that opened it cannot be triggered a second time behind it.
     */
    isPushPending: statusLookupMutation.isPending || isPushInFlight || pendingConfirmation !== undefined,
  }
}
